import { Image } from 'expo-image';
import { View as RNView, StyleSheet } from 'react-native';
import { View } from 'tamagui';

import { radius, space } from '@/constants/tokens';

import PSText from './ui/PSText';

/**
 * AccessPassBanner — the black "check out the Access Pass" card (molecule).
 *
 * The one-tap doorway into the Access Pass: a `$brandDark` slab with the pitch
 * set large on the left and the PS logo mark behind it on the right.
 *
 * The copy is HARDWIRED, not a prop. There's one Access Pass and one sentence
 * that names it — a `title` prop would only invite a second banner through a
 * component whose whole layout (a 3-line text column with art in the gap it
 * leaves) is cut for this one. Same reasoning as `PSUpsellBanner`'s hardwired
 * reel. `onPress` is the only seam: the SCREEN decides where the pass lives.
 *
 * The arrow is the `→` GLYPH inside the sentence, not a `PSIcon` beside it —
 * it has to wrap with the last two words and sit on the text's baseline, and an
 * icon element can't do either inside a wrapping `Text`.
 *
 * ── The art is out of flow, so the text owns the height ────────────────
 * The logo mark lives in a `StyleSheet.absoluteFill` layer, so the card's
 * height is exactly the text block's ("142 hug" in the design). It's anchored
 * to the RIGHT edge because the card stretches to the page gutter.
 *
 * The text column is capped at a PERCENTAGE, not a fixed width: it's the gap
 * left for the art that matters, and that gap has to shrink with the card on a
 * narrow phone or the copy would run under the mark.
 *
 * `expo-image` isn't Tamagui, so `$token` strings don't resolve on it: the
 * tint reads the `color` object straight out of `constants/tokens.ts` — the same
 * bridge `PSIcon` makes with `getTokenValue`, minus the lookup, since we already
 * know which token we want.
 */

// Art placement, in layout px measured off the design at its 358pt card width.
// `fromRight` / `fromTop` name the anchor the mark hangs from (raw layout
// numbers, like the width/height beside them — art coordinates, not spacing).
const ART = {
  logo: {
    source: require('@/assets/images/ps-logo.png'),
    width: 26,
    height: 30,
    fromTop: 24,
    fromRight: space.xxl,
  },
} as const;

// The share of the card the copy may use; the rest is the art's gap.
const TEXT_WIDTH = '85%';

export default function AccessPassBanner({
  onPress,
}: {
  onPress?: () => void;
}) {
  return (
    <View
      alignSelf="stretch"
      bg="$color.brandDark"
      m={-space.xl}
      px={'$space.xxl'}
      py={'$space.xl'}
      overflow="hidden"
      onPress={onPress}
      cursor="pointer"
    >
      {/* Plain RN View + absoluteFill: it carries no design values at all (the
          mark positions itself), so there's nothing here to tokenize, and
          taking the layer out of flow is what keeps the card's height the
          text's. Absolute insets on a Tamagui View would need a `0` space
          token, which the scale deliberately doesn't have. */}
      <RNView style={StyleSheet.absoluteFill} pointerEvents="none">
        <Image
          source={ART.logo.source}
          style={{
            position: 'absolute',
            top: ART.logo.fromTop,
            right: ART.logo.fromRight,
            width: ART.logo.width,
            height: ART.logo.height,
          }}
          contentFit="contain"
        />
      </RNView>
      <PSText.Title
        fontSize="$xl"
        color="$color.textWhite"
        // Tamagui's strict style typing narrows maxWidth to tokens/numbers, so
        // it can't see a percentage — RN takes it fine (same cast as
        // MembershipPlanCard's `width="30%"`).
        // @ts-ignore
        maxWidth={TEXT_WIDTH}
      >
        Want first dibs on this and more?
      </PSText.Title>
      <PSText.Body2
        color="$color.textWhite"
        // Tamagui's strict style typing narrows maxWidth to tokens/numbers, so
        // it can't see a percentage — RN takes it fine (same cast as
        // MembershipPlanCard's `width="30%"`).
        // @ts-ignore
        maxWidth={TEXT_WIDTH}
        my={'$space.md'}
      >
        Unlock secret menus, previews, original events, and member-only
        experiences.
      </PSText.Body2>
      <PSText.H3
        fontWeight="$semibold"
        color="$color.brandSecondary"
        // Tamagui's strict style typing narrows maxWidth to tokens/numbers, so
        // it can't see a percentage — RN takes it fine (same cast as
        // MembershipPlanCard's `width="30%"`).
        // @ts-ignore
        maxWidth={TEXT_WIDTH}
        my={'$space.sm'}
      >
        Explore Access Pass Now
      </PSText.H3>
    </View>
  );
}
